import { toInteger } from 'lodash';
import React, { useContext } from 'react';
import { SliderInput } from '@meitu/saas-ui';
import { type IVectorLayerProps } from '@meitu/saas-editor-core/es/version-types';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { VectorPanelContext } from '../../context';
import './style.less';
import { VectorLayer } from '@meitu/saas-editor-core/es/layers/vector-layer';

export const TextPadding: React.FC = () => {
    const { $t } = useLocaleStore();

    const { layerId } = useContext(VectorPanelContext)!;

    const editor = useEditor();
    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return null;

        const { padding } = textInfo;

        return {
            width: layer.__proactiveWidth || layer.width,
            height: layer.__proactiveHeight || layer.height,
            padding,
        };
    });

    const width = props?.width || 0;
    const height = props?.height || 0;
    const padding = props?.padding || { top: 0, right: 0, bottom: 0, left: 0 };
    const horizontalPadding = padding.left || 0;
    const verticalPadding = padding.top || 0;

    const shortSide = Math.min(width, height);

    const min = 0;
    const max = toInteger(shortSide / 2);

    const handleChangePadding = (
        value: number,
        options: { kind: 'horizontal' | 'vertical'; writeHistory: boolean },
    ) => {
        if (!props) return;

        const nextPadding = { ...padding };
        const integerValue = toInteger(value);

        if (options.kind === 'horizontal') {
            nextPadding.left = integerValue;
            nextPadding.right = integerValue;
        } else {
            nextPadding.top = integerValue;
            nextPadding.bottom = integerValue;
        }

        editor.getInstance(VectorLayer).setTextPadding(layerId, nextPadding, { writeHistory: options.writeHistory });
    };

    return (
        <div className="text-padding">
            <LabelAction title={$t((v) => v.panel.vectorPanel.textInfo.vPadding)} align>
                <SliderInput
                    min={min}
                    max={max}
                    step={1}
                    showStepBtn
                    value={verticalPadding}
                    type="horizontal"
                    onChange={(value) => handleChangePadding(value, { kind: 'vertical', writeHistory: false })}
                    onAfterChange={(value) => handleChangePadding(value, { kind: 'vertical', writeHistory: true })}
                />
            </LabelAction>

            <LabelAction title={$t((v) => v.panel.vectorPanel.textInfo.hPadding)} align>
                <SliderInput
                    min={min}
                    max={max}
                    step={1}
                    showStepBtn
                    value={horizontalPadding}
                    type="horizontal"
                    onChange={(value) => handleChangePadding(value, { kind: 'horizontal', writeHistory: false })}
                    onAfterChange={(value) => handleChangePadding(value, { kind: 'horizontal', writeHistory: true })}
                />
            </LabelAction>
        </div>
    );
};
